/* global $ */

import bog from 'bog';

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import DetectElementResize from 'detect-element-resize';
import { withTheme } from 'styled-components';
import getConfig from 'next/config';

import $ from '../../chart/jquery';
import LoadingShade from '../LoadingShade';

import '../../chart/colorpicker';
import '../../static/chart/chart-bundle.min';

import { setChartIndicator as setChartIndicatorAction, setDataIsLoading as setKlineChartLoadingAction } from '../../actions/load';
import withErrorHandler from '../../utils/withErrorHandler';
import ChartContainer from '../../components/ChartContainer';
import { getIntervals, initCandleChart, setChartSymbol, Indis, saveChartUserSettings } from '../../utils/chart';


const {
  publicRuntimeConfig: { assetPrefix },
} = getConfig();

const CHART_INTERVALS = getIntervals();

const DEFAULT_VIEW_NAME = 'default';
const DEFAULT_INTERVAL_NAME = '1h';
const DEFAULT_INTERVAL = CHART_INTERVALS[DEFAULT_INTERVAL_NAME];
const DEFAULT_CHART_THEME = 'chart-white2';
const DEFAULT_CHART_LINE_TYPE = 'Candle';

const mapStateToProps = ({ tickers, klineUpdates, curIndicators, isDataLoading }, { symbol, lastKlineUpdate }) => {
  const ticker= tickers[symbol];
  // TRY to not cause error on inactive market.
  try {
    const tickSize = ticker.tickSize;
    const minQty = ticker.minTrade;
    const props = {
      ticker,
      tickSize,
      minQty,
      curIndicators,
      lastKlineUpdate: klineUpdates[symbol],
      isDataLoading: isDataLoading?.kline,
    };
    return props;
  } catch (err) {
    return { ticker, curIndicators,  isDataLoading: isDataLoading?.kline};
  }
};

export default
@withTheme
@withErrorHandler
@connect(mapStateToProps,
  { setDataIsLoading: setKlineChartLoadingAction, setCurIndicator: setChartIndicatorAction }
)

// PureComponent because we do not want to rerender if `tickers` is the same.
// https://github.com/facebook/react/blob/d7157651f7b72d9888c0123e191f9b88cd8f41e9/src/renderers/shared/stack/reconciler/ReactCompositeComponent.js#L902-L922
class DefaultChartRenderer extends Component {
  static propTypes = {
    theme: PropTypes.object,
    lastKlineUpdate: PropTypes.object,
    locale: PropTypes.string,
    minQty: PropTypes.number,
    symbol: PropTypes.string.isRequired,
    ticker: PropTypes.object,
    symbols: PropTypes.array,
    tickSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selectedView: PropTypes.string,
    fullScreen: PropTypes.bool,
    interval: PropTypes.number,
    intervalName: PropTypes.string,
    lineType: PropTypes.string,
    setDataIsLoading: PropTypes.func,
    curIndicators: PropTypes.object,
    setCurIndicator: PropTypes.func,
    indicatorName: PropTypes.string,
    drawLineType: PropTypes.string,
    dataSetup: PropTypes.number,
    isDataLoading: PropTypes.bool,
  };

  static defaultProps = {
    theme: {},
    lastKlineUpdate: null,
    locale: 'en',
    minQty: 0.01,
    ticker: {},
    symbols: [],
    tickSize: '0.01',
    selectedView: DEFAULT_VIEW_NAME,
    fullScreen: false,
    interval: 60,
    lineType: DEFAULT_CHART_LINE_TYPE,
    intervalName: DEFAULT_INTERVAL_NAME,
    setDataIsLoading: () => {},
    setCurIndicator: () => {},
    curIndicators: {},
    indicatorName: '',
    drawLineType: '',
    dataSetup: 0,
    isDataLoading: true,
  }

  shouldComponentUpdate({
    selectedView,
    symbol,
    interval,
    lastKlineUpdate,
    intervalName,
    theme,
  }) {
    const defaultIsNow = this.props.selectedView === DEFAULT_VIEW_NAME;
    const defaultIsNext = selectedView === DEFAULT_VIEW_NAME;

    // default is next or we are leaving default (so hide it)
    return defaultIsNext ||
      (defaultIsNow && !defaultIsNext) ||
      // symbol or interval changed, update chart even if hidden
      (this._chart && symbol !== this.props.symbol) ||
      (this._chart && interval !== this.props.interval) ||
      (this._chart && theme !== this.props.theme);
  }

  componentDidUpdate({
    symbol: prevSymbol, interval: prevInterval,
    lineType: prevLineType, indicatorName: preIndicatorName,
    curIndicators: preCurIndicators,
    drawLineType: preDrawLineType,
    theme: prevTheme,
    dataSetup: prevDataSetup,
  }) {
    const {
      symbol,
      ticker,
      selectedView,
      fullScreen,
      interval,
      lineType,
      indicatorName,
      theme,
      curIndicators,
      intervalName,
      lastKlineUpdate,
      dataSetup,
    } = this.props;
    const quoteAsset = ticker.qa;
    // clear data if symbol does not exist
    if (typeof quoteAsset !== 'string') {
      this._chart.clearData();
      return;
    }

    if (!this._chart) return;

    if (theme !== prevTheme) {
      const el = this._chart.internals().element;
      $(el).fadeOut(0, () => {
        this._chart.onTheme(theme.chartTheme);
        $(el).fadeIn(100);
      });
    }

    if (dataSetup !== prevDataSetup) {
      window.Data && window.Data.setup();
    }

    if (lastKlineUpdate && lastKlineUpdate[intervalName]) {
      const candlesticks = lastKlineUpdate[intervalName];
      const { k: ticks } = candlesticks;
      const {
        o: open,
        h: high,
        l: low,
        c: close,
        v: volume,
        q: quoteVolume,
        t: tickTime,
      } = ticks;
      let bar = [
        open,
        high,
        low,
        close,
        volume,
        quoteVolume,
      ];
      bar = bar.map(parseFloat);
      bar.unshift(tickTime);

      if (window.Data && this._chart) window.Data.onBar(bar, interval);
    }

    // resize chart if fullscreen toggled
    if (selectedView === DEFAULT_VIEW_NAME) {
      // TODO: this spams klines updates!
      // this._chart.setResolution(interval);
      this._init();

      // fullscreen toggled?
      if (fullScreen) {
        this._chart.resize();
      }
    }
    if (symbol !== prevSymbol) {
      this.props.setDataIsLoading({ key: 'kline', isLoading: true });
    }
    // update the "candlesticks" chart on symbol change
    if (symbol !== prevSymbol && prevSymbol) {
      this._chart.clearData();
      setChartSymbol({ ...this.props, quoteAsset });
    }

    // interval changed?
    if (interval !== prevInterval) {
      this.props.setDataIsLoading({ key: 'kline', isLoading: true });
      this._chart.setResolution(interval);
    }

    if (lineType !== prevLineType) {
      this._chart.removeIndicatorByname(['ma', 'ma', 'ma']);
      this._chart.removeIndicatorByname(['avl']);
      this._removeOver(this.props.curIndicators.curOver);

      if (lineType === 'Line') {
        this._chart.setLastColorIndex(0);

        this._chart.addOrUpdateIndicator('avl');
        this.props.setCurIndicator({curOver: 'AVL'});
        if(this.props.pro) saveChartUserSettings(this._chart);
      } else {
        this._chart.setLastColorIndex(1);
        this._chart.addOrUpdateIndicator('ma', 7);
        this._chart.addOrUpdateIndicator('ma', 25);
        this._chart.addOrUpdateIndicator('ma', 99);
        this.props.setCurIndicator({curOver: 'MA'});
        if(this.props.pro) saveChartUserSettings(this._chart);
      }

      if (lineType) {
        this._chart.setMode(lineType);
      }
    }
    if (indicatorName !== preIndicatorName) {
      this._setIndicator(indicatorName);
    }
    if (!curIndicators.curInd) {
      this._removeIndicator(preCurIndicators.curInd);
    }
    if (!curIndicators.curOver) {
      this._removeOver(preCurIndicators.curOver);
    }
    if (this.props.drawLineType === 'clear') {
      this.line.clean();
    } else if (this.props.drawLineType === 'deleteSelected') {
      this.line.deleteLine();
    } else if (this.props.drawLineType !== preDrawLineType) {
      this.line.add(this.props.drawLineType);
      this._chart.setCrossHair(false);
    }
  }

  componentDidMount() {
    // TODO: not a great way to do this
    this.props.setDataIsLoading({ key: 'kline', isLoading: true });
    window.jQuery(this._chartContainer.current)
    .on('blur', '.icontrol input', () =>
      saveChartUserSettings(this._chart));
    const { locale } = this.props;
    if (locale === 'cn') window.localStorage.setItem('lang', 'cn');
    else if (window.localStorage) window.localStorage.setItem('lang', 'en');

    this._init();
  }

  componentWillUnmount() {
    this._resizeHandler &&
        DetectElementResize.removeResizeListener(this._chartContainer.current, this._resizeHandler);
    this._chart && this._chart.clearData();
  }

  _init() {
    if (!this._chart) {
      if (__DEV__) bog.info('Init Kline chart.');
      const {
        theme,
        symbol,
        ticker,
        tickSize,
        minQty,
        interval,
        lineType,
        pro,
      } = this.props;

      // candlesticks chart
      const containerEl = this._chartContainer.current;
      const { chart, line, curInd, curOver } = initCandleChart({
        symbol,
        quoteAsset: ticker.qa,
        themeName: theme.chartTheme || DEFAULT_CHART_THEME,
        tickSize,
        minQty,
        defaultInterval: parseInt(interval, 10) || DEFAULT_INTERVAL,
        defaultLineType: lineType,
        containerEl,
        pro,
      });
      this._chart = chart;
      this.line = line;
      this.props.setCurIndicator({curOver: curOver, curInd: curInd})
      const self = this;
      this.props.setDataIsLoading({ key: 'kline', isLoading: true });
      window.Data.onLoaded(() => {
        self.props.setDataIsLoading({ key: 'kline', isLoading: false });
      });

      // call chart.resize() when the element resizes
      DetectElementResize.addResizeListener(
        this._chartContainer.current, this._resizeHandler = () => {
          this._chart && this._chart.resize();
        });
      this._chart.AddRemoveIndicatorCallback(( ) => {
        if (this.props.pro) saveChartUserSettings(this._chart);
      })
    }
  }

  _chartContainer = React.createRef();

  _setIndicator = (indicatorName) => {
    // $scope.jszbLists=false
    const tanIndicator = Indis[indicatorName];

    if (this._chart.isIndicator(tanIndicator)) {
      // 是Indecator
      this._removeIndicator(this.props.curIndicators.curInd);
      this._chart.addOrUpdateIndicator(tanIndicator);
      // $scope.curzb = indicatorName;
      this.props.setCurIndicator({ curInd: indicatorName });
      if (this.props.pro) saveChartUserSettings(this._chart,indicatorName);
    } else {
      this._removeOver(this.props.curIndicators.curOver);
      if (tanIndicator === 'ema' || tanIndicator === 'ma') {
        this._chart.addOrUpdateIndicator(tanIndicator, 7);
        this._chart.addOrUpdateIndicator(tanIndicator, 25);
        this._chart.addOrUpdateIndicator(tanIndicator, 99);
      } else {
        this._chart.addOrUpdateIndicator(tanIndicator);
      }

      this.props.setCurIndicator({ curOver: indicatorName });
      if (this.props.pro) saveChartUserSettings(this._chart);
    }

  }

  _removeIndicator = (ind) => {
    const indicatorName = Indis[ind];
    if (indicatorName === 'ema' || indicatorName === 'ma') {
      this._chart.removeIndicatorByname([indicatorName]);
      this._chart.removeIndicatorByname([indicatorName]);
      this._chart.removeIndicatorByname([indicatorName]);
    } else {
      this._chart.removeIndicatorByname([indicatorName]);
    }
    if (this.props.pro) saveChartUserSettings(this._chart);
  }

  _removeOver = (ind) => {
    const indicatorName = Indis[ind];
    if (indicatorName === 'ema' || indicatorName === 'ma') {
      this._chart.removeIndicatorByname([indicatorName]);
      this._chart.removeIndicatorByname([indicatorName]);
      this._chart.removeIndicatorByname([indicatorName]);
    } else {
      this._chart.removeIndicatorByname([indicatorName]);
    }

    if (this.props.pro) saveChartUserSettings(this._chart);
  }

  render() {
    const {hidden,isDataLoading} = this.props

    return (
      <Fragment>
        <LoadingShade hidden={hidden|| !isDataLoading} />
        { this.props.pro ? <link rel="stylesheet" href={`${assetPrefix}/static/chart/css/kline.css`} /> : null }
        <ChartContainer
          ref={this._chartContainer}
          hidden={this.props.selectedView !== DEFAULT_VIEW_NAME}
          fullScreen={this.props.fullScreen}
          selectedView={this.props.selectedView}
          className={'chartContainer'}
        >
          {/* <canvas ref={this._setCanvasRef} /> */}
        </ChartContainer>
      </Fragment>
    );
  }
}
